import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JournalPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [newJournal, setNewJournal] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadJournals();
  }, [token]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadJournals = async () => {
    try {
      const res = await fetch(`${API}/api/profile/journal`, { headers });
      if (res.ok) setJournals(await res.json());
    } catch { toast.error('Could not load journal entries'); }
  };

  const addJournal = async (e) => {
    e.preventDefault();
    if (!newJournal.title || !newJournal.content) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/profile/journal`, { method: 'POST', headers, body: JSON.stringify(newJournal) });
      if (res.ok) {
        const entry = await res.json();
        setJournals([entry, ...journals]);
        setNewJournal({ title: '', content: '' });
        toast.success('Journal entry saved');
      }
    } catch { toast.error('Could not save entry'); }
    setSaving(false);
  };

  const deleteJournal = async (id) => {
    await fetch(`${API}/api/profile/journal/${id}`, { method: 'DELETE', headers });
    setJournals(journals.filter(j => j.entry_id !== id));
  };

  if (!user) return null;

  return (
    <section data-testid="journal-page" className="journal-page">
      <div className="journal-header">
        <h1 className="journal-title">Your Journal</h1>
        <p className="journal-subtitle">Write your thoughts. Track your transformation.</p>
      </div>

      <div className="journal-content">
        <form onSubmit={addJournal} className="journal-write-form" data-testid="journal-form">
          <input
            data-testid="journal-title-input"
            type="text"
            placeholder="Title..."
            value={newJournal.title}
            onChange={(e) => setNewJournal({ ...newJournal, title: e.target.value })}
            className="journal-input"
            required
          />
          <textarea
            data-testid="journal-content-input"
            placeholder="What's on your mind, what's in your heart..."
            value={newJournal.content}
            onChange={(e) => setNewJournal({ ...newJournal, content: e.target.value })}
            className="journal-input journal-input--textarea"
            rows={6}
            required
          />
          <button data-testid="journal-submit" type="submit" className="village-btn village-btn--primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Entry'}
          </button>
        </form>

        <div className="journal-entries" data-testid="journal-entries">
          {journals.map((j) => (
            <div key={j.entry_id} className="journal-entry-card" data-testid={`journal-${j.entry_id}`}>
              <div className="journal-entry-top">
                <h3 className="journal-entry-title">{j.title}</h3>
                <button className="journal-entry-delete" onClick={() => deleteJournal(j.entry_id)} aria-label="Delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <p className="journal-entry-body">{j.content}</p>
              <span className="journal-entry-date">{new Date(j.created_at).toLocaleDateString()}</span>
            </div>
          ))}
          {journals.length === 0 && <p className="journal-empty">Your journal is empty. Start writing your story.</p>}
        </div>
      </div>
    </section>
  );
}
